import { useState, useRef, Fragment } from 'react'
import { dispatchCreditUpdateEvent, startUseCredit } from '../../utils/creditSystem';
import { cloneVoice } from '../../utils/api';
import { dispatchMyVoicesUpdateEvent } from '../../utils/event';

function VoiceCloneModal({ onClose, content }) {
    const [voiceName, setVoiceName] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [cloneLoading, setCloneLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [nameError, setNameError] = useState('');
    const [fileError, setFileError] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [fileId, setFileId] = useState(null);
    const [clonedVoiceId, setClonedVoiceId] = useState('');
    const [audioUrl, setAudioUrl] = useState(null);
    const fileInputRef = useRef(null);
    const audioRef = useRef(null);
    try {
        const validateVoiceName = (name) => {
            const namePattern = /^[a-zA-Z][a-zA-Z0-9]{7,}$/;
            if (!namePattern.test(name)) {
                setNameError('聲音名稱必須至少8個字符，包含字母和數字，並以字母開頭');
                return false;
            }
            setNameError('');
            return true;
        };

        const validateAudioFile = (file) => {
            if (!file) {
                setFileError('請選擇音頻檔案');
                return false;
            }

            // Check file type
            const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/x-m4a'];
            if (!validTypes.includes(file.type)) {
                setFileError('請上傳 mp3, m4a 或 wav 格式的檔案');
                return false;
            }

            // Check file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                setFileError('檔案大小不能超過 5MB');
                return false;
            }

            setFileError('');
            return true;
        };

        const handleNameChange = (e) => {
            const name = e.target.value;
            setVoiceName(name);
            if (name) {
                validateVoiceName(name);
            } else {
                setNameError('');
            }
        };

        const handleFileChange = (e) => {
            const file = e.target.files[0];
            setAudioFile(file);
            if (file) {
                validateAudioFile(file);
            } else {
                setFileError('');
            }
        };

        const handleDrag = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (e.type === "dragenter" || e.type === "dragover") {
                setDragActive(true);
            } else if (e.type === "dragleave") {
                setDragActive(false);
            }
        };

        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
            
            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                const file = e.dataTransfer.files[0];
                setAudioFile(file);
                validateAudioFile(file);
            }
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setSuccess(false);
            setApiResponse(null);
            setFileId(null);
            setClonedVoiceId('');
            setAudioUrl(null);

            // Validate inputs
            const isNameValid = validateVoiceName(voiceName);
            const isFileValid = validateAudioFile(audioFile);

            if (!isNameValid || !isFileValid) {
                return;
            }

            setIsLoading(true);

            try {
                // Upload the audio file to Minimax
                const uploadFormData = new FormData();
                uploadFormData.append('file', audioFile);
                uploadFormData.append('purpose', 'voice_clone');
                uploadFormData.append('name', voiceName);

                const resp = await cloneVoice(uploadFormData);
                if (!resp.success) {
                    throw new Error(resp.error);
                }

                setApiResponse(resp.data.data);
                setFileId(resp.data.file_id);
                setClonedVoiceId(voiceName);
                setSuccess(true);

                dispatchCreditUpdateEvent(resp.data.credit);
                dispatchMyVoicesUpdateEvent(resp.data.items);
            } catch (error) {
                console.error('Process error:', error);
                setError(error.message || '聲音克隆失敗，請稍後再試');
                
                // If we have a file ID but cloning failed, still show it
                if (!fileId && error.fileId) {
                    setFileId(error.fileId);
                }
            } finally {
                setIsLoading(false);
            }
        };

        const handleCloseModal = () => {
            // Clean up audio URL if exists
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
            onClose();
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto" data-name="voice-clone-modal">
                <div className="bg-white rounded-xl w-full max-w-md shadow-2xl my-8" data-name="modal-content">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">
                                語音克隆
                            </h2>
                            <button 
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                disabled={isLoading}
                                data-name="close-button"
                            >
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>

                        {error && (
                            <div 
                                className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded"
                                role="alert"
                                data-name="error-message"
                            >
                                <div className="flex">
                                    <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                    <p className="text-red-700">{error}</p>
                                </div>
                            </div>
                        )}

                        {success && (
                            <div 
                                className="bg-green-100 p-4 mb-6 rounded-lg border border-green-200"
                                data-name="success-message"
                            >
                                <div className="flex items-center mb-3">
                                    <i className="fas fa-check-circle text-green-500 text-xl mr-3"></i>
                                    <h3 className="text-lg font-semibold text-green-800">聲音克隆成功！</h3>
                                </div>
                                <div className="flex items-center justify-between bg-green-50 p-3 rounded-md">
                                    <div>
                                        <p className="text-sm text-green-700 font-medium">聲音 ID:</p>
                                        <p className="text-xl font-bold text-green-800">{clonedVoiceId}</p>
                                    </div>
                                    <div className="bg-green-600 text-white px-3 py-1 rounded text-sm">
                                        已準備好使用
                                    </div>
                                </div>
                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={handleCloseModal}
                                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                                        data-name="done-button"
                                    >
                                        <i className="fas fa-check mr-2"></i>
                                        完成
                                    </button>
                                </div>
                            </div>
                        )}

                        {fileId && !success && (
                            <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <h3 className="font-medium mb-2">檔案 ID：</h3>
                                <div className="bg-gray-100 p-2 rounded overflow-auto">
                                    <code>{fileId}</code>
                                </div>
                            </div>
                        )}

                        {apiResponse && !success && (
                            <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <h3 className="font-medium mb-2">API 回應：</h3>
                                <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                                    {JSON.stringify(apiResponse, null, 2)}
                                </pre>
                            </div>
                        )}

                        {!success && (
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label htmlFor="voiceName" className="block text-sm font-medium text-gray-700 mb-2">
                                        克隆聲音名稱 <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="voiceName"
                                        value={voiceName}
                                        onChange={handleNameChange}
                                        className={`w-full px-4 py-2 border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all`}
                                        placeholder="例如：PeterPan001"
                                        disabled={isLoading}
                                        data-name="voice-name-input"
                                    />
                                    {nameError && (
                                        <p className="mt-1 text-sm text-red-500">{nameError}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">
                                        最少8個字符；必須包含字母和數字，並以字母開頭
                                    </p>
                                </div>

                                <div className="mb-6">
                                    <label htmlFor="audioFile" className="block text-sm font-medium text-gray-700 mb-2">
                                        上傳音頻檔案 <span className="text-red-500">*</span>
                                    </label>
                                    <div 
                                        className={`border-2 border-dashed ${dragActive ? 'border-indigo-500 bg-indigo-50' : fileError ? 'border-red-500' : 'border-gray-300'} rounded-lg p-4 text-center`}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            id="audioFile"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept=".mp3,.m4a,.wav"
                                            className="hidden"
                                            disabled={isLoading}
                                            data-name="audio-file-input"
                                        />
                                        <label htmlFor="audioFile" className="cursor-pointer">
                                            <div className="flex flex-col items-center">
                                                <i className="fas fa-music text-4xl text-indigo-500 mb-2"></i>
                                                {audioFile ? (
                                                    <div>
                                                        <p className="text-sm font-medium">{audioFile.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <p className="text-gray-600 mb-1">點擊上傳或拖放檔案</p>
                                                        <p className="text-xs text-gray-500">支援 mp3, m4a, wav 格式</p>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                    {fileError && (
                                        <p className="mt-1 text-sm text-red-500">{fileError}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">
                                        時長：10秒 至 5分鐘，大小：小於 5MB
                                    </p>
                                </div>
                                
                                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                                    <div className="flex items-center">
                                        <i className="fas fa-coins text-yellow-500 mr-2"></i>
                                        <p className="text-yellow-700">此操作將消耗 1 點數</p>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-4">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                        disabled={isLoading}
                                        data-name="cancel-button"
                                    >
                                        取消
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                                        disabled={isLoading}
                                        data-name="submit-button"
                                    >
                                        {isLoading ? (
                                            <Fragment>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                {uploadLoading ? '上傳中...' : cloneLoading ? '克隆中...' : '處理中...'}
                                            </Fragment>
                                        ) : (
                                            <Fragment>
                                                <i className="fas fa-microphone mr-2"></i>
                                                開始克隆
                                            </Fragment>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        );
    } catch (error) {
        console.error('VoiceCloneModal component error:', error);
        reportError(error);
        return null;
    }
}

export default VoiceCloneModal;